import { reactive, toRefs, readonly } from 'vue'
import type { DeepReadonly, Ref, ToRefs } from 'vue'

import { DEFAULT_FONT_SIZE, clampSize } from '~/lib/constants/labels'
import type { SelectedDataSet } from '~/composables/useDrillHoleSettings'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'

type State = {
  size: number
  isAutoScale: boolean
  fontOptions: { id: number; name: string; classes: string }[]
}

export type UseLabelTextSize = {
  setSize: (newSize: number) => void
  toggleAutoScale: () => void
} & ToRefs<DeepReadonly<State>>

export function useLabelTextSize({
  drillDataSetManager,
  drillDataSet,
}: {
  drillDataSetManager: DrillDataSetManager | null | undefined
  drillDataSet: DeepReadonly<Ref<SelectedDataSet | undefined>>
}): UseLabelTextSize {
  const state = reactive<State>({
    size: DEFAULT_FONT_SIZE,
    isAutoScale: true,
    fontOptions: [
      { id: 400, name: 'Headline', classes: 'text-3xl' },
      { id: 300, name: 'Subheading', classes: 'text-2xl' },
      { id: 200, name: 'Body', classes: 'text-xl' },
    ],
  })

  watch(
    drillDataSet,
    drillDataSet => {
      if (!drillDataSet) return

      if (drillDataSetManager?.drillLabelManager) {
        state.size = drillDataSetManager.getLabelsTextSize(drillDataSet.id)
        state.isAutoScale = drillDataSetManager.drillLabelManager.isSceneAutoScale()
      } else {
        state.size = DEFAULT_FONT_SIZE
        state.isAutoScale = true
      }
    },
    { immediate: true },
  )

  function setSize(newSize: number) {
    if (!drillDataSetManager || !drillDataSet.value) return

    const textSize = clampSize(newSize)
    state.size = textSize

    drillDataSetManager.setLabelsTextSize(drillDataSet.value.id, textSize)
  }

  function toggleAutoScale() {
    if (!drillDataSetManager || !drillDataSet.value) return

    state.isAutoScale = !state.isAutoScale
    drillDataSetManager?.setLabelsAutoScale(drillDataSet.value.id, state.isAutoScale)
    state.size = drillDataSetManager?.getLabelsTextSize(drillDataSet.value.id)
  }

  return {
    ...toRefs(readonly(state)),
    setSize,
    toggleAutoScale,
  }
}
